import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Button, Alert, Chip, Stack, Snackbar,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Skeleton, TextField, Tabs, Tab, MenuItem, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CalculateIcon from '@mui/icons-material/Calculate';
import Layout from '../components/Layout';
import {
  fetchPriceOutliers, fetchUnnormalizedLines, correctLineNormalization, recomputeAlertsNow,
  OutlierLine, UnnormalizedLine, apiErrorText } from '../api/client';
import {
  ProvenanceBlock, fmtEUR, fmtEURUnit, fmtQty, fmtConfidence,
} from '../components/LineProvenance';

type TabKey = 'outliers' | 'unnormalized';

const UNIT_OPTIONS = ['kg', 'l', 'Stück'];

/** Accepts "9,69" and "9.69". Returns NaN on garbage. */
const parseDe = (s: string): number => parseFloat((s ?? '').replace(',', '.').trim());

/** Best guess for the base unit from the printed invoice unit, used to pre-fill the select. */
const guessUnit = (printed?: string | null): string => {
  const u = (printed || '').toLowerCase();
  if (/^(kg|kilo)/.test(u)) return 'kg';
  if (/^(l|lt|ltr|liter)/.test(u)) return 'l';
  if (/^(st|stk|stück|stueck|pc)/.test(u)) return 'Stück';
  return '';
};

interface UnnormEdit {
  mode: 'base' | 'price';
  value: string;
  unit: string;
}

export default function PreisPruefungPage() {
  const [tab, setTab] = useState<TabKey>('outliers');
  const [outliers, setOutliers] = useState<OutlierLine[]>([]);
  const [unnorm, setUnnorm] = useState<UnnormalizedLine[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorOutliers, setErrorOutliers] = useState<string | null>(null);
  const [errorUnnorm, setErrorUnnorm] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [recomputing, setRecomputing] = useState(false);
  const [edits, setEdits] = useState<Record<number, string>>({});
  const [unnormEdits, setUnnormEdits] = useState<Record<number, UnnormEdit>>({});
  const [savingId, setSavingId] = useState<number | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true); setErrorOutliers(null); setErrorUnnorm(null);
    const [o, u] = await Promise.allSettled([fetchPriceOutliers(4), fetchUnnormalizedLines(300)]);
    if (o.status === 'fulfilled') setOutliers(o.value || []);
    else setErrorOutliers(apiErrorText(o.reason) || 'Fehler beim Laden der Ausreißer');
    if (u.status === 'fulfilled') setUnnorm(u.value || []);
    else setErrorUnnorm(apiErrorText(u.reason) || 'Fehler beim Laden der nicht normalisierten Positionen');
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // ---- Outlier correction (unchanged behaviour: enter the real €/Einheit) ----
  const saveOutlier = async (r: OutlierLine) => {
    const val = parseDe(edits[r.line_id] ?? '');
    if (!val || val <= 0) { setToast('Bitte einen gültigen €/Einheit-Preis eingeben.'); return; }
    setSavingId(r.line_id);
    try {
      const res = await correctLineNormalization(r.line_id, val, true);
      setToast(`Korrigiert: ${r.cluster_name || r.name} → ${fmtEURUnit(val)}/${r.normalized_unit || 'Einheit'} (${res.lines_updated} Positionen).`);
      setOutliers(prev => prev.filter(x => x.line_id !== r.line_id));
    } catch (e: any) {
      setToast(apiErrorText(e) || 'Speichern fehlgeschlagen');
    } finally { setSavingId(null); }
  };

  // ---- Unnormalized correction: base quantity OR €/Einheit ----
  const getUnnormEdit = (r: UnnormalizedLine): UnnormEdit =>
    unnormEdits[r.line_id] ?? { mode: 'base', value: '', unit: r.normalized_unit || guessUnit(r.unit) };

  const setUnnormEdit = (lineId: number, patch: Partial<UnnormEdit>, base: UnnormEdit) =>
    setUnnormEdits(p => ({ ...p, [lineId]: { ...base, ...patch } }));

  /** Derived €/Einheit for the live preview + save. null = invalid input. */
  const derivedUnitPrice = (r: UnnormalizedLine, e: UnnormEdit): number | null => {
    const v = parseDe(e.value);
    if (!v || v <= 0 || !isFinite(v)) return null;
    if (e.mode === 'price') return v;
    if (r.total == null || !isFinite(r.total) || r.total <= 0) return null;
    return r.total / v;
  };

  const saveUnnorm = async (r: UnnormalizedLine) => {
    const e = getUnnormEdit(r);
    if (!e.unit) { setToast('Bitte die Basis-Einheit (kg / l / Stück) auswählen.'); return; }
    const val = derivedUnitPrice(r, e);
    if (val == null) {
      setToast(e.mode === 'base'
        ? 'Bitte eine gültige Basis-Menge eingeben (Summe der Position muss > 0 sein).'
        : 'Bitte einen gültigen €/Einheit-Preis eingeben.');
      return;
    }
    setSavingId(r.line_id);
    try {
      const res = await correctLineNormalization(r.line_id, val, true, e.unit);
      setToast(`Normalisiert: ${r.cluster_name || r.name} → ${fmtEURUnit(val)}/${e.unit} (${res.lines_updated} Positionen).`);
      setUnnorm(prev => prev.filter(x => x.line_id !== r.line_id));
    } catch (err: any) {
      setToast(apiErrorText(err) || 'Speichern fehlgeschlagen');
    } finally { setSavingId(null); }
  };

  const doRecompute = async () => {
    setRecomputing(true);
    try {
      const res = await recomputeAlertsNow();
      setToast(`Warnungen neu berechnet · Sparpotenzial ${fmtEUR(res.sparpotenzial_eur_yearly)}/Jahr (${res.alerts} Warnungen).`);
    } catch (e: any) {
      setToast(apiErrorText(e) || 'Neuberechnung fehlgeschlagen');
    } finally { setRecomputing(false); }
  };

  const intro = useMemo(() => tab === 'outliers'
    ? 'Positionen, deren Preis pro Einheit stark vom Produkt-Durchschnitt abweicht — meist ein falsch gelesenes Gebinde. Prüfe die Herkunft (Rohzeile, Gebinde-Lesung, Rechnung), trage den echten €/Einheit-Preis ein; wir rechnen die Verpackungsgröße zurück und wenden sie auf alle Rechnungen dieses Artikels an.'
    : 'Positionen ohne €/Einheit: Gebinde oder Inhalt konnten nicht sicher gelesen werden. Sie fließen in keinen Preisvergleich ein, bis sie korrigiert sind. Trage die Basis-Menge (kg / l / Stück der ganzen Position) aus der Rechnung ein — oder direkt den €/Einheit-Preis.',
  [tab]);

  return (
    <Layout>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>Preis-Prüfung</Typography>
        <Typography variant="body2" color="text.secondary">{intro}</Typography>
      </Box>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2 }}
        alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between">
        <Tabs value={tab} onChange={(_, v: TabKey) => setTab(v)} textColor="primary" indicatorColor="secondary"
          sx={{ minHeight: 40, '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontWeight: 600 } }}>
          <Tab value="outliers" iconPosition="end"
            icon={<Chip size="small" label={loading ? '…' : outliers.length}
              color={outliers.length > 0 ? 'error' : 'default'} sx={{ ml: 1 }} />}
            label="Ausreißer" />
          <Tab value="unnormalized" iconPosition="end"
            icon={<Chip size="small" label={loading ? '…' : unnorm.length}
              color={unnorm.length > 0 ? 'warning' : 'default'} sx={{ ml: 1 }} />}
            label="Nicht normalisiert" />
        </Tabs>
        <Stack direction="row" spacing={1.5} flexWrap="wrap">
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={refresh} disabled={loading}>
            Aktualisieren
          </Button>
          <Button variant="contained" startIcon={<CalculateIcon />} onClick={doRecompute} disabled={recomputing}
            sx={{ bgcolor: 'secondary.main', '&:hover': { bgcolor: 'secondary.dark' } }}>
            {recomputing ? 'Wird berechnet…' : 'Warnungen neu berechnen'}
          </Button>
        </Stack>
      </Stack>

      {/* ------------------------------ Ausreißer ------------------------------ */}
      {tab === 'outliers' && (
        <>
          {errorOutliers && <Alert severity="error" sx={{ mb: 2 }}>{errorOutliers}</Alert>}
          {loading ? (
            <Skeleton variant="rounded" height={280} />
          ) : outliers.length === 0 ? (
            <Alert severity="success">Keine auffälligen Preise. Die Normalisierung sieht sauber aus.</Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small" sx={{ '& td, & th': { verticalAlign: 'top' } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ minWidth: 200 }}>Produkt</TableCell>
                    <TableCell align="right" sx={{ minWidth: 170 }}>Gelesen vs. Ø Produkt</TableCell>
                    <TableCell sx={{ minWidth: 320 }}>Herkunft</TableCell>
                    <TableCell sx={{ minWidth: 210 }}>Korrektur €/Einheit</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {outliers.map((r) => (
                    <TableRow key={r.line_id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{r.cluster_name || r.name}</Typography>
                        <Typography variant="caption" color="text.secondary" component="div">{r.name}</Typography>
                        <Typography variant="caption" color="text.secondary" component="div">
                          {r.supplier} · Art-Nr {r.article_no}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                          <Typography variant="body2" color="error.main" fontWeight={700}>
                            {fmtEURUnit(r.unit_price_per_einheit)}/{r.normalized_unit || 'Einheit'}
                          </Typography>
                          <Chip size="small" color={r.ratio > 1 ? 'error' : 'warning'} variant="outlined"
                            label={r.ratio >= 1 ? `${r.ratio.toFixed(1)}×` : `1/${(1 / r.ratio).toFixed(1)}×`} />
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          Ø Produkt {fmtEURUnit(r.cluster_median)}/{r.normalized_unit || 'Einheit'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <ProvenanceBlock line={r} onNotice={setToast} />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <TextField size="small" placeholder={`z.B. ${fmtQty(r.cluster_median)}`} value={edits[r.line_id] ?? ''}
                            onChange={(e) => setEdits(p => ({ ...p, [r.line_id]: e.target.value }))}
                            onKeyDown={(e) => { if (e.key === 'Enter') saveOutlier(r); }}
                            sx={{ width: 130 }}
                            InputProps={{ endAdornment: <Typography variant="caption" color="text.secondary">€/{r.normalized_unit || 'Einh.'}</Typography> }} />
                          <Button size="small" variant="contained" disabled={savingId === r.line_id}
                            onClick={() => saveOutlier(r)}>Speichern</Button>
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          Gilt für alle Positionen dieses Artikels.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {/* --------------------------- Nicht normalisiert --------------------------- */}
      {tab === 'unnormalized' && (
        <>
          {errorUnnorm && <Alert severity="error" sx={{ mb: 2 }}>{errorUnnorm}</Alert>}
          {loading ? (
            <Skeleton variant="rounded" height={280} />
          ) : unnorm.length === 0 ? (
            <Alert severity="success">Alle Positionen haben einen €/Einheit-Preis. Nichts zu tun.</Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small" sx={{ '& td, & th': { verticalAlign: 'top' } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ minWidth: 200 }}>Produkt</TableCell>
                    <TableCell sx={{ minWidth: 320 }}>Herkunft</TableCell>
                    <TableCell sx={{ minWidth: 300 }}>Korrektur</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {unnorm.map((r) => {
                    const e = getUnnormEdit(r);
                    const preview = derivedUnitPrice(r, e);
                    return (
                      <TableRow key={r.line_id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{r.cluster_name || r.name}</Typography>
                          <Typography variant="caption" color="text.secondary" component="div">{r.name}</Typography>
                          <Typography variant="caption" color="text.secondary" component="div">
                            {r.supplier} · Art-Nr {r.article_no}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Alert severity="warning" icon={false} sx={{ py: 0, px: 1, mb: 0.75, '& .MuiAlert-message': { py: 0.5 } }}>
                            <Typography variant="caption">
                              Gebinde/Inhalt konnte nicht sicher gelesen werden
                              {' '}(Methode {r.pack_method || 'unbekannt'}, Sicherheit {fmtConfidence(r.pack_confidence)})
                              {' '}— bitte aus der Rechnung prüfen.
                            </Typography>
                          </Alert>
                          <ProvenanceBlock line={r} onNotice={setToast} />
                        </TableCell>
                        <TableCell>
                          <Stack spacing={1}>
                            <ToggleButtonGroup size="small" exclusive value={e.mode}
                              onChange={(_, v: 'base' | 'price' | null) => { if (v) setUnnormEdit(r.line_id, { mode: v, value: '' }, e); }}
                              sx={{ '& .MuiToggleButton-root': { textTransform: 'none', py: 0.25, px: 1.25 } }}>
                              <ToggleButton value="base">Basis-Menge</ToggleButton>
                              <ToggleButton value="price">€/Einheit</ToggleButton>
                            </ToggleButtonGroup>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <TextField size="small" value={e.value}
                                placeholder={e.mode === 'base' ? 'z.B. 9' : 'z.B. 9,69'}
                                onChange={(ev) => setUnnormEdit(r.line_id, { value: ev.target.value }, e)}
                                onKeyDown={(ev) => { if (ev.key === 'Enter') saveUnnorm(r); }}
                                sx={{ width: 110 }}
                                InputProps={{ endAdornment: e.mode === 'price'
                                  ? <Typography variant="caption" color="text.secondary">€/</Typography>
                                  : undefined }} />
                              <TextField size="small" select value={e.unit} sx={{ width: 100 }}
                                onChange={(ev) => setUnnormEdit(r.line_id, { unit: ev.target.value }, e)}
                                SelectProps={{ displayEmpty: true }}>
                                <MenuItem value="" disabled><em>Einheit</em></MenuItem>
                                {UNIT_OPTIONS.map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
                              </TextField>
                              <Button size="small" variant="contained" disabled={savingId === r.line_id}
                                onClick={() => saveUnnorm(r)}>Speichern</Button>
                            </Stack>
                            <Typography variant="caption" color={preview != null ? 'success.main' : 'text.secondary'}>
                              {e.mode === 'base'
                                ? (preview != null
                                  ? `${fmtEUR(r.total)} ÷ ${fmtQty(parseDe(e.value))} ${e.unit || 'Einheit'} = ${fmtEURUnit(preview)}/${e.unit || 'Einheit'}`
                                  : `Wie viele ${e.unit || 'kg / l / Stück'} deckt die ganze Position (${fmtEUR(r.total)}) ab?`)
                                : (preview != null
                                  ? `→ ${fmtEURUnit(preview)}/${e.unit || 'Einheit'} wird gespeichert`
                                  : 'Echter Preis pro Basis-Einheit laut Rechnung.')}
                            </Typography>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      <Snackbar open={!!toast} autoHideDuration={5000} onClose={() => setToast(null)} message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Layout>
  );
}
